
import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown, Instagram, Facebook } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
const ApplyNowModal = lazy(() => import("@/components/ApplyNowModal"));
import { trackPhoneClick } from "@/utils/analytics";

const servicesDropdown = [
  { name: "Merchant Processing", href: "/services/merchant-processing" },
  { name: "POS Systems", href: "/services/pos-systems" },
  { name: "Payment Gateways", href: "/services/payment-gateways" },
  { name: "Cash Discount Program", href: "/services/cash-discount-program" },
  { name: "High-Risk Processing", href: "/services/high-risk-processing" },
  { name: "Restaurant Payments", href: "/services/restaurant-payment-processing" },
  { name: "Small Business Services", href: "/services/small-business-merchant-services" },
  { name: "All Services →", href: "/services" },
];

const industriesDropdown = [
  { name: "Restaurants", href: "/industries/restaurant-processing" },
  { name: "Retail Stores", href: "/industries/retail-processing" },
  { name: "Smoke Shops", href: "/industries/smoke-shop-processing" },
  { name: "Bars & Nightclubs", href: "/industries/bar-nightclub-processing" },
  { name: "Convenience Stores", href: "/industries/convenience-store-processing" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const navigationItems = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/services", dropdown: servicesDropdown },
    { name: "Industries", href: "/industries/restaurant-processing", dropdown: industriesDropdown },
    { name: "POS Systems & Equipment", href: "/products" },
    { name: "About Us", href: "/about" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Mobile section headers for grouped nav
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);

  const handleDropdownEnter = (name: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  useEffect(() => {
    setOpenDropdown(null);
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none"
      >
        Skip to main content
      </a>
      <header className="bg-background/95 backdrop-blur-md shadow-sm border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-18">
          <Link to="/" className="flex items-center space-x-2 hover-scale">
            <img 
              src="/processing-bros-logo-dark.png" 
              className="w-[120px] sm:w-[150px] md:w-[180px] h-auto transition-all duration-300 block dark:hidden"
              alt="Processing Bros - Your Success, Our Process" 
              width="180" height="40" fetchPriority="high" loading="eager" decoding="async"
            />
            <img 
              src="/processing-bros-logo-light.png" 
              className="w-[120px] sm:w-[150px] md:w-[180px] h-auto transition-all duration-300 hidden dark:block"
              alt="Processing Bros - Your Success, Our Process" 
              width="180" height="40" fetchPriority="high" loading="eager" decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigationItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && handleDropdownEnter(item.name)}
                onMouseLeave={() => item.dropdown && handleDropdownLeave()}
              >
                <Link
                  to={item.href}
                  className={`text-sm font-medium transition-all duration-300 hover:text-primary relative group flex items-center gap-1 py-4 ${
                    isActive(item.href) 
                      ? 'text-primary' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {item.name}
                  {item.dropdown && <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full ${
                    isActive(item.href) ? 'w-full' : ''
                  }`}></span>
                </Link>

                {/* Dropdown */}
                {item.dropdown && openDropdown === item.name && (
                  <div className="absolute top-full left-0 mt-0 w-64 bg-popover border border-border rounded-lg shadow-xl z-50 py-2 animate-fade-in">
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className="block px-4 py-2.5 text-sm text-popover-foreground hover:bg-muted transition-colors"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="https://www.facebook.com/profile.php?id=61565136844981" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-muted-foreground hover:text-primary transition-colors" 
              aria-label="Follow us on Facebook"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a 
              href="https://www.instagram.com/processingbros/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-muted-foreground hover:text-primary transition-colors" 
              aria-label="Follow us on Instagram"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a 
              href="tel:9543992230" 
              className="flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-300 hover-scale"
              onClick={() => trackPhoneClick("header_desktop")}
            >
              <Phone className="h-4 w-4 mr-2" />
              (954) 399-2230
            </a>
            <Button variant="default" onClick={() => setIsApplyModalOpen(true)}>
              Apply Now
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border animate-fade-in">
            <div className="py-6 space-y-1 bg-background/95 backdrop-blur-md">
              <Link to="/" className="block px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg mx-4" onClick={() => setIsMenuOpen(false)}>Home</Link>
              
              {/* Services Accordion */}
              <div className="mx-4">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                >
                  Services
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="pl-4 space-y-1">
                    {servicesDropdown.map((sub) => (
                      <Link key={sub.href} to={sub.href} className="block px-6 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg" onClick={() => setIsMenuOpen(false)}>
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Industries Accordion */}
              <div className="mx-4">
                <button
                  onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                  className="flex items-center justify-between w-full px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                >
                  Industries
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileIndustriesOpen && (
                  <div className="pl-4 space-y-1">
                    {industriesDropdown.map((sub) => (
                      <Link key={sub.href} to={sub.href} className="block px-6 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg" onClick={() => setIsMenuOpen(false)}>
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/products" className="block px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg mx-4" onClick={() => setIsMenuOpen(false)}>POS Systems & Equipment</Link>
              <Link to="/about" className="block px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg mx-4" onClick={() => setIsMenuOpen(false)}>About Us</Link>
              <Link to="/blog" className="block px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg mx-4" onClick={() => setIsMenuOpen(false)}>Blog</Link>
              <Link to="/contact" className="block px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg mx-4" onClick={() => setIsMenuOpen(false)}>Contact</Link>

              <div className="px-4 pt-6 border-t border-border mt-4 space-y-4">
                <div className="flex items-center gap-4">
                  <a href="https://www.facebook.com/profile.php?id=61565136844981" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Facebook">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href="https://www.instagram.com/processingbros/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                </div>
                <a href="tel:9543992230" className="flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors" onClick={() => trackPhoneClick("header_mobile")}>
                  <Phone className="h-4 w-4 mr-2" />
                  (954) 399-2230
                </a>
                <Button className="w-full" onClick={() => { setIsMenuOpen(false); setIsApplyModalOpen(true); }}>
                  Apply Now
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </header>

      <Suspense fallback={null}>
        <ApplyNowModal open={isApplyModalOpen} onOpenChange={setIsApplyModalOpen} />
      </Suspense>
    </>
  );
};

export default Header;
